import type { PropsWithChildren } from 'react';

import { emitMixpanelUserEvent, UserTrackingEvents } from '@revelio/core';
import {
  IconButton,
  InfoTipIcon,
  Popover,
  type PopoverProps,
} from '@revelio/ui';

import { useOptionalRevelioPageId } from '../PageConfigProvider/PageConfigProvider';
import { useSafeChart } from './ChartProvider';
import styles from './PlotInfoComp.module.css';

type PlotConfigObject = {
  iconBoxSize: number;
  popoverPlacement: NonNullable<PopoverProps['placement']>;
};

export interface PlotInfoCompProps extends PropsWithChildren {
  plotInfoConfig: PlotConfigObject | null;
  topLeft?: React.ReactNode;
  disableInfoPopup?: boolean;
}

export const PlotInfoComp = ({
  plotInfoConfig,
  topLeft,
  disableInfoPopup = false,
  children,
}: PlotInfoCompProps) => {
  const { item } = useSafeChart();
  const pageId = useOptionalRevelioPageId();
  return (
    <span className={styles.infoRow}>
      {children && !disableInfoPopup && (
        <Popover
          openOnHover
          openDelay={500}
          placement={plotInfoConfig?.popoverPlacement}
          arrow
          className={styles.tooltip}
          onOpenChange={(open) => {
            if (!open) return;
            emitMixpanelUserEvent(UserTrackingEvents.TOOLTIP_OPEN, {
              tooltip_id: 'chart_info',
              ...(item?.id !== undefined && { chart_id: item.id }),
              ...(pageId !== undefined && { page_id: pageId }),
            });
          }}
          trigger={
            <IconButton
              className={styles.infoButton}
              aria-label="plot info"
              data-testid="plot-info-icon"
              icon={
                <InfoTipIcon size={(plotInfoConfig?.iconBoxSize ?? 2.5) * 5} />
              }
            />
          }
        >
          <div data-testid="plot-info-body">{children}</div>
        </Popover>
      )}
      {topLeft}
    </span>
  );
};
